
import { IDropdownOption } from '@fluentui/react';
import { EControlType } from 'components/ControlWrapper';
import { IGCDataLight } from 'global/Reducer/dataSlice';
import { formatDate, parseDate } from 'global/Utility/TSFunctions';
import { bcbs_groupconfigurationsnapshot } from 'model/AttributeOptionSets.type';
import { bcbs_config_category } from 'model/CRM';
import { Ibcbs_groupconfigurationsnapshot_extended } from 'model/CRM.type';
import {
    IBenefit as FAM_IBenefit,
    IFlexibleField as FAM_IFlexibleField,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,

    IGroupNumber as FAM_IGroupNumber,
} from 'model/FlexibleAttributeModel';
import {
    IFlexibleFormAttributeDefinition,
    IFlexibleFormAttributeSetDefinition,
    IFlexibleFormCategoryDefinition,
    IFlexibleFormDefinition,
    IFlexibleFormSectionDefinition,
    IFlexibleFormTabDefinition
} from 'model/FlexibleFormModel';
import { ICompareConfiguration } from 'views/BlueRelationship/CompareLookupDialog/CompareLookupDialog';
import { InjectIntoFlexibleFormDef, IPrimeData, TPrimeSummaryKeys } from 'views/BlueRelationship/Pharmacy/PrimeHelpers';
import ComparisonEngine, {
    CompareProperties,
    ECompareStatus,
    EModelContext,
    IComparable,
    ICompareContext,
    IDataEntityLike,
    IDataEntityLikeMerged,
    IDataFlexibleFieldLikeMerged,
    IFlexibleFieldValuesCompared,
    modelContextToFriendlyName,
    ShouldSkipAttribute
} from './ComparisonEngine';

export const PRIMARY_PCP_SPECIALIST_MAP: Record<string, string> = {
    "primary": "Primary",
    "pcp": "PCP",
    "specialist": "Specialist",
}

export interface IPropertyDefinition {
    name: string;
    order: number;
    logicalName: string;
    mapping?: () => string
}

interface IRowVM {
    propertyDefinition: IPropertyDefinition;
    origin?: string;
    target?: string;
    changed: boolean;


    parent: IAttributeSetVM | INonStandardVM | IEntityVM | null;
    mergeAction: EMergeVMStatus;
}


export interface IPropertyValueVM {
    key: string;
    label: string;

    attributeDefinition: IFlexibleFormAttributeDefinition;
    fieldComparison: IComparable<FAM_IFlexibleField, IFlexibleFieldValuesCompared> | undefined
    origin?: string;
    target?: string;
    changed: boolean;
    mergeAction: EMergeVMStatus;

    parent: IAttributeSetVM | INonStandardVM | IEntityVM;
}

export interface IFlexibleFieldValueVM {
    context?: ICompareContext[];
    onOrigin: FAM_IFlexibleField | undefined;
    onTarget: FAM_IFlexibleField | undefined;
    merged: IDataFlexibleFieldLikeMerged | undefined;
}

export interface IFlexibleFieldVM {
    key: string;

    // TODO delete this
    label: string;
    changed: boolean;
    attributeDefinition: IFlexibleFormAttributeDefinition;

    // fieldVM: IComparable<FAM_IFlexibleField, IDataFlexibleFieldLikeMerged> | undefined;

    fieldVM: IFlexibleFieldValueVM | undefined;

    // TODO delete this
    mergeAction: EMergeVMStatus;

    parent: IAttributeSetVM | INonStandardVM | IEntityVM;
}



export interface IEntityVM {
    uniqueId: string;
    id: string;
    name: string;

    compareResult: ECompareVMStatus;
    changed: boolean;
    formDefinition: IFlexibleFormDefinition;
    friendlyName: string;
    propertiesVM: IPropertiesVM;
    referencesVM: IPropertiesVM | null;
    tiersVM: IPropertiesVM | null;
    tabsVM: ITabVM[];
    entityCompared: IComparable<IDataEntityLike, IDataEntityLikeMerged>;
    mergeAction: EMergeVMStatus;

    // THIS MUST STAY TEXT
    context: EModelContext;

    type: ESetVMType;
    parent: IEntityVM;


    _debugAttributeSetVM: IAttributeSetVM[];
    _debugNonStandardVM: INonStandardVM[];
    _debugAttributesVM: IFlexibleFieldVM[];
}

export enum ESetVMType {
    nonStandard,
    attributeSet,
    category,
}


export interface IAttributeSetVM extends Pick<
    IEntityVM,
    'id' | 'name' | 'formDefinition' | 'changed' | 'propertiesVM' | 'compareResult' | 'mergeAction' | 'parent' | 'type' | 'friendlyName'
> {

    // the attribute that is the attribute set
    definition: IFlexibleFormAttributeDefinition;

    // (Not Part of schema) the tab/section (on the owning entity's form) that hosts this attribute set
    tabDefinition: IFlexibleFormTabDefinition;
    // (Not Part of schema)
    sectionDefinition: IFlexibleFormSectionDefinition;

    // to simplify we are not going to render tabs/sections for now, just directly attributes
    attributesVM: IFlexibleFieldVM[];


}

export interface INonStandardVM extends Pick<
    IEntityVM,
    'id' | 'name' | 'formDefinition' | 'changed' | 'propertiesVM' | 'compareResult' | 'mergeAction' | 'parent' | 'type' | 'friendlyName'
> {
    // (Not Part of schema) the tab (on the owning entity's form) that hosts this non-standard's section
    tabDefinition: IFlexibleFormTabDefinition;

    sectionDefinition: IFlexibleFormSectionDefinition;

    // to simplify we are not going to render tabs/sections for now, just directly attributes
    attributesVM: IFlexibleFieldVM[];
}

interface IAttributeSetFormVM {
    formDefinition: IFlexibleFormAttributeSetDefinition;
    attributeSets: IAttributeSetVM[];
}


interface ISectionVM {
    sectionDefinition: IFlexibleFormSectionDefinition;
    attributesVM: IFlexibleFieldVM[];
    attributeSetsVM: IAttributeSetFormVM[]
    nonStandardsVM: INonStandardVM[];
    changed: boolean;

}

interface ITabVM {
    tabDefinition: IFlexibleFormTabDefinition;
    sectionsVM: ISectionVM[];
    changed: boolean;
}

interface IPropertiesVM {
    rowsVM: IRowVM[];
    changed: boolean;
}

export enum ECompareVMStatus {
    Unknown,

    // match on origin and target, children match as well, has not been changed
    Identical,

    // match on origin and target, but has been changed, or children changed
    Modified,

    // new on target, not on origin
    OnTargetOnly,

    // not on target, only on origin
    OnOriginOnly,
}

export interface IEntitiesContainerVM {
    context: EModelContext;
    entities: IEntityVM[];
    changed: boolean;
    title: string;
}



export enum EMergeVMStatus {

    // for apps that are not merge app
    // for entities that are not supported for merge,
    // either due to their status (for example, deactivated entity) or other reasons,
    // we will set their merge action to Unsupported and hide merge options on UI
    Disabled,

    // default action, no change will be made to target upon merge
    // * ~ EUseTarget (use target value) for fields, and for attribute sets, use target attributes without merging fields even if it's possible
    UseTarget,

    // for entities that only exist on target, remove from target upon merge
    RemoveTarget,

    // for entities that only exist on origin, add to target upon merge
    AddOrigin,

    // for modified entities, pick value from origin
    UseOrigin,

    // for modified entities, root level, whether to use origin value, target value, or to merge fields if it's possible
    Merge,


    Pend,

    // for entities that are set to be ignored by parent-level merge decisions,
    // they will follow the merge action of their parent entity, and will not have
    // an independent merge action, for example, if a benefit is set to ParentOverride,
    // all non-standard, attribute sets and fields under that benefit will
    // follow the merge action of the benefit,
    ParentOverride,
    ParentOverrideUseOrigin,
    ParentOverrideUseTarget,

}

export type TDefaultMergeOption = EMergeVMStatus.UseOrigin | EMergeVMStatus.UseTarget | EMergeVMStatus.Pend;

export const EMergeAction_FriendlyLabels: Record<EMergeVMStatus, string> = {
    [EMergeVMStatus.Disabled]: 'Disabled',
    [EMergeVMStatus.UseTarget]: 'Keep Target Unchanged',
    [EMergeVMStatus.RemoveTarget]: 'Remove From Target',
    [EMergeVMStatus.AddOrigin]: 'Add To Target From Origin',
    [EMergeVMStatus.UseOrigin]: 'Replace Target With Origin',
    [EMergeVMStatus.Merge]: 'Merge (Origin With Target)',
    [EMergeVMStatus.ParentOverride]: 'Parent Override',
    [EMergeVMStatus.ParentOverrideUseOrigin]: 'Parent Override (Use Origin)',
    [EMergeVMStatus.ParentOverrideUseTarget]: 'Parent Override (Use Target)',
    [EMergeVMStatus.Pend]: 'Pending',
};

export const EMergeAction_DiagnosisLabels: Record<EMergeVMStatus, string> = {
    [EMergeVMStatus.Disabled]: 'Disabled',
    [EMergeVMStatus.RemoveTarget]: 'RemoveTarget',
    [EMergeVMStatus.AddOrigin]: 'AddOrigin',
    [EMergeVMStatus.UseTarget]: 'UseTarget',
    [EMergeVMStatus.UseOrigin]: 'UseOrigin',
    [EMergeVMStatus.Merge]: 'Merge',
    [EMergeVMStatus.ParentOverride]: 'ParentOverride',
    [EMergeVMStatus.ParentOverrideUseOrigin]: 'ParentOverrideUseOrigin',
    [EMergeVMStatus.ParentOverrideUseTarget]: 'ParentOverrideUseTarget',
    [EMergeVMStatus.Pend]: 'Pending',
};

export interface IGroupConfigurationComparisonVM {
    summary: IPropertiesVM;
    groupConfiguration: IEntitiesContainerVM;
    nonStandardTemplate: IEntitiesContainerVM;
    plan: IEntitiesContainerVM;
    benefit: IEntitiesContainerVM;
    feesAndPremium: IEntitiesContainerVM;
    feesSummary: IEntitiesContainerVM;
    groupNumber: IEntitiesContainerVM;
    cesNumber: IEntitiesContainerVM;
    department: IEntitiesContainerVM;
    stopLossSummary: IEntitiesContainerVM;
    stopLoss: IEntitiesContainerVM;
    accountsReceivable: IEntitiesContainerVM;
}

export interface IPrimePharmacyComparisonVM {
    summary: IPropertiesVM;
    prime: IEntityVM;
    groupNumbers: IEntitiesContainerVM;
}


export class ComparisonViewModelGenerator {

    static DefaultPropertyDefinition: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        }
    };

    static ConfigurationSummaryPropertyDefinition: Record<string, IPropertyDefinition> = {
        "snapshot_name": {
            name: "Name",
            logicalName: "snapshot_name",
            order: 10,
        },
        "snapshot_effectiveDate": {
            name: "Effective Date",
            logicalName: "snapshot_effectiveDate",
            order: 30,
        },
        "snapshot_statuscode": {
            name: "Status",
            logicalName: "snapshot_statuscode",
            order: 20,
        },
        "snapshot_createdon": {
            name: "Created On",
            logicalName: "snapshot_createdon",
            order: 40,
        },
        "snapshot_createdby": {
            name: "Created By",
            logicalName: "snapshot_createdby",
            order: 50,
        },
        "snapshot_modifiedby": {
            name: "Modified By",
            logicalName: "snapshot_modifiedby",
            order: 60,
        },
        "snapshot_modifiedon": {
            name: "Modified On",
            logicalName: "snapshot_modifiedon",
            order: 70,
        },
        "snapshot_fundingType": {
            name: "Funding Type",
            logicalName: "snapshot_fundingType",
            order: 80,
        },
        "snapshot_changeType": {
            name: "Change Type",
            logicalName: "snapshot_changeType",
            order: 85,
        },
        "snapshot_dualFunded": {
            name: "Dual Funded",
            logicalName: "snapshot_dualFunded",
            order: 90,
        },
        "erisa": {
            name: "Erisa",
            logicalName: "erisa",
            order: 100,
        },
        "snapshot_bcbs_originatingsnapshotid_name": {
            name: "Originating Snapshot",
            logicalName: "snapshot_bcbs_originatingsnapshotid_name",
            order: 110,
        },
        "snapshot_bcbs_originatingsnapshot2id_name": {
            name: "Originating Snapshot 2",
            logicalName: "snapshot_bcbs_originatingsnapshot2id_name",
            order: 115,
        },
        "grandFathered": {
            name: "Grandfathered",
            logicalName: "grandFathered",
            order: 120,
        },
        "lineOfBusiness": {
            name: "Line Of Business",
            logicalName: "lineOfBusiness",
            order: 130,
        },
        "snapshot_mnil": {
            name: "MNIL",
            logicalName: "snapshot_mnil",
            order: 140,
        },
        "snapshot_reconciliation": {
            name: "Reconciliation",
            logicalName: "snapshot_reconciliation",
            order: 150,
        },
        "opportunity": {
            name: "Opportunity",
            logicalName: "opportunity",
            order: 160,
        },
        "snapshot_releaseid_name": {
            name: "Release",
            logicalName: "snapshot_releaseid_name",
            order: 170,
        }
    }

    static GroupConfigurationPropertyDefinition: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 10,
        },
        "bcbs_shortname": {
            name: "Short Name",
            logicalName: "bcbs_shortname",
            order: 15,
        },
        "status": {
            name: "Status",
            logicalName: "status",
            order: 20,
        },
        "effectiveDate": {
            name: "Effective Date",
            logicalName: "effectiveDate",
            order: 30,
        },
        "lineOfBusiness": {
            name: "Line Of Business",
            logicalName: "lineOfBusiness",
            order: 130,
        },
        "changeType": {
            name: "Change Type",
            logicalName: "changeType",
            order: 85,
        },
        "dualFunded": {
            name: "Dual Funded",
            logicalName: "dualFunded",
            order: 90,
        },
        "erisa": {
            name: "Erisa",
            logicalName: "erisa",
            order: 100,
        },
        "fundingType": {
            name: "Funding Type",
            logicalName: "fundingType",
            order: 80,
        },
        "grandFathered": {
            name: "Grandfathered",
            logicalName: "grandFathered",
            order: 120,
        },
        "MNIL": {
            name: "MNIL",
            logicalName: "mnil",
            order: 140,
        },
        "reconciliation": {
            name: "Reconciliation",
            logicalName: "reconciliation",
            order: 150,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 160,
        },
    }

    static BenefitPropertyDefinition: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
        "fundingType": {
            name: "Funding Type",
            logicalName: "fundingType",
            order: 3,
        },
        "grandFathered": {
            name: "Grand Fathered",
            logicalName: "grandFathered",
            order: 4,
        },
        "product": {
            name: "Product",
            logicalName: "product",
            order: 5,
        },
        "tierSetRef": {
            name: "TierSetRef",
            logicalName: "tierSetRef",
            order: 6,
        },
        "status": {
            name: "Status",
            logicalName: "status",
            order: 7,
        },
    }

    static PlanPropertyDefinition: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
        "fundingType": {
            name: "Funding Type",
            logicalName: "fundingType",
            order: 3,
        },
        "grandFathered": {
            name: "Grand Fathered",
            logicalName: "grandFathered",
            order: 4,
        },
        "billingOnlyPlan": {
            name: "Billing Only Plan",
            logicalName: "billingOnlyPlan",
            order: 5,
        },
        "feesAndPremiumsRef": {
            name: "Fees and Premiums",
            logicalName: "feesAndPremiumsRef",
            order: 6,
        },

    }

    static GroupNumberPropertyDefinitions: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
        "status": {
            name: "Status",
            logicalName: "status",
            order: 3,
        },
        "groupNumber": {
            name: "Group Number",
            logicalName: "groupNumber",
            order: 4,
        },
        "cesNumberRef": {
            name: "CES Number",
            logicalName: "cesNumberRef",
            order: 5,
        },
        "planRef": {
            name: "Plan",
            logicalName: "planRef",
            order: 6,
        },
        "stopLossRef": {
            name: "Stop Loss",
            logicalName: "stopLossRef",
            order: 7,
        },
        "accountsReceivableRef": {
            name: "Accounts Receivable",
            logicalName: "accountsReceivableRef",
            order: 8,
        },
        "prefix": {
            name: "Prefix",
            logicalName: "prefix",
            order: 9,
        },
        "suffix": {
            name: "Suffix",
            logicalName: "suffix",
            order: 10,
        },
        "base": {
            name: "Base",
            logicalName: "base",
            order: 11,
        },

    }

    static PrimePharmacyPropertyDefinition: Record<TPrimeSummaryKeys, IPropertyDefinition> = {
        "snapshot_bcbs_name": {
            name: "Name",
            logicalName: "snapshot_bcbs_name",
            order: 10,
        },
        "plan_name": {
            name: "Plan Name",
            logicalName: "plan_name",
            order: 20,
        },
        "plan_grandfathered": {
            name: "Grandfather Status",
            logicalName: "plan_grandfathered",
            order: 20,
        },
        "snapshot_bcbs_effectivedate": {
            name: "Effective Date",
            logicalName: "snapshot_bcbs_effectivedate",
            order: 30,
        },
        "snapshot_bcbs_fundingtypecodename": {
            name: "Funding Type",
            logicalName: "snapshot_bcbs_fundingtypecodename",
            order: 40,
        },
        "json_lineofbusiness": {
            name: "Line Of Business",
            logicalName: "json_lineofbusiness",
            order: 50,
        },
        "json_erisa": {
            name: "Erisa",
            logicalName: "json_erisa",
            order: 60,
        },
        "account_accountpartnerplan": {
            name: "Partner Plan",
            logicalName: "account_accountpartnerplan",
            order: 70,
        },
        "drugindicator": {
            name: "Drug Indicator",
            logicalName: "drugindicator",
            order: 90,
        },
        "bcbs_ongoingbenefitperiodruns": {
            name: "Ongoing Benefit Period Runs",
            logicalName: "bcbs_ongoingbenefitperiodruns",
            order: 100,
        },
        "bcbs_ongoingbenefitperiodrunsthrough1": {
            name: "Ongoing Benefit Period Runs Through",
            logicalName: "bcbs_ongoingbenefitperiodrunsthrough1",
            order: 110,
        },
        "pharmacyshortname": {
            name: "Pharmacy Short Name",
            logicalName: "pharmacyshortname",
            order: 120,
        },
        "medicalshortname": {
            name: "Medical Short Name",
            logicalName: "medicalshortname",
            order: 130,
        },
        "bcbs_coordinatebenefitsonpharmacy": {
            name: "Coordinate Benefits On Pharmacy",
            logicalName: "bcbs_coordinatebenefitsonpharmacy",
            order: 135,
        },
        "carrier": {
            name: "Carrier",
            logicalName: "carrier",
            order: 138,
        },
        "clientnumbers": {
            name: "Client Numbers",
            logicalName: "clientnumbers",
            order: 140,
        },
        "newclientnumber": {
            name: "New Client Number",
            logicalName: "newclientnumber",
            order: 150,
        },
        "pharmacyplandesign": {
            name: "Pharmacy Plan Design",
            logicalName: "pharmacyplandesign",
            order: 160,
        },
        "newpharmacyplandesign": {
            name: "New Pharmacy Plan Design",
            logicalName: "newpharmacyplandesign",
            order: 170,
        },

    }

    static CESNumberPropertyDefinitions: Record<string, IPropertyDefinition> = {
        "name": {
            name: "CES Number",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
    }

    static DepartmentPropertyDefinitions: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Department Number",
            logicalName: "name",
            order: 1,
        },
        "description": {
            name: "Description",
            logicalName: "description",
            order: 2,
        },
        "cesNumberRef": {
            name: "CES Number",
            logicalName: "cesNumberRef",
            order: 3,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 4,
        },
    }

    static NonStandardTemplatePropertyDefinitions: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
        "referenceOnly": {
            name: "Reference Only",
            logicalName: "referenceOnly",
            order: 3,
        },
        "linkedCategoryName": {
            name: "Linked Category",
            logicalName: "linkedCategoryName",
            order: 4,
        },
    }

    static FeesAndPremiumPropertyDefinition: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
    }

    static FeesSummaryPropertyDefinition: Record<string, IPropertyDefinition> = {
        "category": {
            name: "Category",
            logicalName: "category",
            order: 1,
        },
        "feesAndPremiumName": {
            name: "Fees and Premium",
            logicalName: "feesAndPremiumName",
            order: 2,
        },
        "Employee Only Rate": {
            name: "Employee Only Rate",
            logicalName: "Employee Only Rate",
            order: 3,
        },
        "Family Rate": {
            name: "Family Rate",
            logicalName: "Family Rate",
            order: 4,
        },
        "Employee + Child(ren) Rate": {
            name: "Employee + Child(ren) Rate",
            logicalName: "Employee + Child(ren) Rate",
            order: 5,
        },
        "Employee + Spouse Rate": {
            name: "Employee + Spouse Rate",
            logicalName: "Employee + Spouse Rate",
            order: 6,
        },
        "Employee + 1 (Child) Rate": {
            name: "Employee + 1 (Child) Rate",
            logicalName: "Employee + 1 (Child) Rate",
            order: 7,
        },
        "Two-Person Rate": {
            name: "Two-Person Rate",
            logicalName: "Two-Person Rate",
            order: 8,
        },
        "Employee + 2 Dependents Rate": {
            name: "Employee + 2 Dependents Rate",
            logicalName: "Employee + 2 Dependents Rate",
            order: 9,
        },
        "Concession Amount": {
            name: "Concession Amount",
            logicalName: "Concession Amount",
            order: 10,
        },
    }

    static StopLossSummaryPropertyDefinition: Record<string, IPropertyDefinition> = {
        "stopLossTypeName": {
            name: "Stop Loss Type",
            logicalName: "stopLossTypeName",
            order: 1,
        },
        "category": {
            name: "Category",
            logicalName: "category",
            order: 2,
        },
        "feesAndPremiumName": {
            name: "Fees and Premium",
            logicalName: "feesAndPremiumName",
            order: 3,
        },
        "Employee Only Rate": {
            name: "Employee Only Rate",
            logicalName: "Employee Only Rate",
            order: 4,
        },
        "Family Rate": {
            name: "Family Rate",
            logicalName: "Family Rate",
            order: 5,
        },
        "Employee + Child(ren) Rate": {
            name: "Employee + Child(ren) Rate",
            logicalName: "Employee + Child(ren) Rate",
            order: 6,
        },
        "Employee + Spouse Rate": {
            name: "Employee + Spouse Rate",
            logicalName: "Employee + Spouse Rate",
            order: 7,
        },
        "Employee + 1 (Child) Rate": {
            name: "Employee + 1 (Child) Rate",
            logicalName: "Employee + 1 (Child) Rate",
            order: 8,
        },
        "Two-Person Rate": {
            name: "Two-Person Rate",
            logicalName: "Two-Person Rate",
            order: 9,
        },
        "Employee + 2 Dependents Rate": {
            name: "Employee + 2 Dependents Rate",
            logicalName: "Employee + 2 Dependents Rate",
            order: 10,
        },
        "Concession Amount": {
            name: "Concession Amount",
            logicalName: "Concession Amount",
            order: 11,
        },
    }

    static StopLossPropertyDefinition: Record<string, IPropertyDefinition> = structuredClone(ComparisonViewModelGenerator.FeesAndPremiumPropertyDefinition);

    static AccountsReceivablePropertyDefinition: Record<string, IPropertyDefinition> = structuredClone(ComparisonViewModelGenerator.FeesAndPremiumPropertyDefinition);

    static NonStandardPropertyDefinition: Record<string, IPropertyDefinition> = {
        "name": {
            name: "Name",
            logicalName: "name",
            order: 1,
        },
        "deactivated": {
            name: "Deactivated",
            logicalName: "deactivated",
            order: 2,
        },
        "referenceOnly": {
            name: "Reference Only",
            logicalName: "referenceOnly",
            order: 3,
        },
        "partOfTemplate": {
            name: "Part Of Template",
            logicalName: "partOfTemplate",
            order: 4,
        }
    }

    /**
     * Generates a properties view model by comparing the origin and target records.
     * @param origin The original record.
     * @param target The target record.
     * @param getPropertyDefinition A function that returns the property definitions.
     * @returns The generated properties view model.
     */
    public static GeneratePropertiesModel(
        origin: Record<string, string | null | undefined>,
        target: Record<string, string | null | undefined>,
        getPropertyDefinition: () => Record<string, IPropertyDefinition>
    ): IPropertiesVM {

        const propertyDefinition = getPropertyDefinition();

        const onMergeProperties = CompareProperties(origin, target);

        const propertiesVM: IPropertiesVM = {
            rowsVM: [],
            changed: false,
        };

        Object.entries(onMergeProperties ?? {}).forEach(([key, eachCombinedProperty]) => {

            const pd = propertyDefinition[key];

            if (!pd) {
                throw new Error(`Property definition for key "${key}" not found. Skipping property.`);
            }

            const propertyVM: IRowVM = {
                propertyDefinition: pd,
                origin: eachCombinedProperty.onOrigin,
                target: eachCombinedProperty.onTarget,
                changed: false,
                parent: null,
                mergeAction: EMergeVMStatus.Disabled,
            };
            propertiesVM.rowsVM.push(propertyVM);

            // if the property is modified, mark it as changed, and also mark the entire propertiesVM as changed
            // so that we can easily show a change indicator at the summary level without having to loop through all properties again
            if (eachCombinedProperty.compareStatus !== ECompareStatus.Identical) {
                propertyVM.changed = true;
                propertiesVM.changed = true;
            }
        });

        propertiesVM.rowsVM.sort((a, b) => a.propertyDefinition.order - b.propertyDefinition.order);
        return propertiesVM;
    }


    /**
     * Generates a full view model for an entity by comparing the origin and target records.
     * @param flexibleFormDefinition The flexible form definition.
     * @param entityCompared The entity being compared.
     * @param state The current state.
     * @param propertyDefinitionsFactory A function that returns the property definitions.
     * @param compareConfiguration The comparison engine instruction filters.
     * @returns The generated full view model for the entity.
     */
    public static GenerateCategoryModel(
        flexibleFormDefinition: IFlexibleFormCategoryDefinition,
        entityCompared: IComparable<IDataEntityLike, IDataEntityLikeMerged>,
        config: FAM_IGroupConfigurationConfig,
        propertyDefinitionsFactory: () => Record<string, IPropertyDefinition>,
        compareConfiguration: ICompareConfiguration | null,
        mergeUserPreferredOption: TDefaultMergeOption | null,
        context: EModelContext | null,
    ): IEntityVM {

        const propertyDefinitions: Record<string, IPropertyDefinition> = propertyDefinitionsFactory
            ? propertyDefinitionsFactory()
            : ComparisonViewModelGenerator.DefaultPropertyDefinition;

        let displayName = entityCompared.onTarget?.name ?? entityCompared.onOrigin?.name ?? "?";

        switch (context) {
            // for benefits,
            // prepend bcbs_name so the benefit can be identified quickly in the UI
            case EModelContext.benefits: {
                const bcbs_name = (entityCompared?.onTarget as FAM_IBenefit)?.bcbs_name ?? (entityCompared?.onOrigin as FAM_IBenefit)?.bcbs_name;

                if (bcbs_name)
                    displayName = bcbs_name + ' → ' + displayName;

                break;
            }

            case EModelContext.groupNumbers:
                displayName = (entityCompared?.onTarget as FAM_IGroupNumber)?.groupNumber ?? (entityCompared?.onOrigin as FAM_IGroupNumber)?.groupNumber ?? displayName;
                break;

            default:
                break;
        }


        const entityVM: IEntityVM = {
            // if there is no key, generate a random id, this is needed for rendering lists of entities that don't have a natural key
            id: entityCompared.merged?.key ?? '?',
            uniqueId: `${context ?? EModelContext.unknown}-${entityCompared.merged?.key ?? '?'}`,
            name: entityCompared.onTarget?.name ?? entityCompared.onOrigin?.name ?? "?",
            entityCompared: entityCompared,
            formDefinition: flexibleFormDefinition,
            friendlyName: displayName,
            // const friendlyName = entity.onMerge?.properties["name"]?.onOrigin + " ↔ " + entity.onMerge?.properties["name"]?.onTarget;
            tabsVM: [],
            tiersVM: null,
            referencesVM: null,
            propertiesVM: {
                rowsVM: [],
                changed: false,
            },
            changed: false,
            compareResult: ECompareVMStatus.Unknown,
            mergeAction: EMergeVMStatus.UseTarget,
            context: context ?? EModelContext.unknown,

            parent: entityCompared as any,
            type: ESetVMType.category,

            _debugAttributeSetVM: [],
            _debugNonStandardVM: [],
            _debugAttributesVM: [],
        };

        // if (context === EModelContext.groupNumbers) {
        //     entityVM.name = (entityCompared?.onTarget as FAM_IGroupNumber)?.groupNumber ?? (entityCompared?.onOrigin as FAM_IGroupNumber)?.groupNumber ?? displayName;
        // }

        const tabs = entityVM.tabsVM;

        // FAM - Properties
        // -----------------------------------------------------
        if (entityCompared.merged?.mergedProperties) {

            Object.entries(entityCompared.merged.mergedProperties).forEach(([key, mergedProperty]) => {

                const propertyDefinition = propertyDefinitions[key];

                if (!propertyDefinition) {
                    console.warn(`Property definition for key "${entityCompared.context?.map(t => t.name).join(", ")}" "${key}" not found. Skipping property.`);
                    return;
                }

                const row: IRowVM = {
                    propertyDefinition: propertyDefinition,
                    origin: mergedProperty.onOrigin,
                    target: mergedProperty.onTarget,
                    changed: false,
                    parent: entityVM,
                    // properties don't have a merge action, they are just for display purposes, so we will set it to disabled
                    mergeAction: EMergeVMStatus.Disabled,
                };

                entityVM.propertiesVM.rowsVM.push(row);

                // change detection: 
                // if the property is modified, 
                // mark it as changed,
                // and also mark the entire propertiesVM as changed
                // so that we can easily show a change indicator at the summary level without having to loop through all properties again
                if (mergedProperty.compareStatus !== ECompareStatus.Identical) {
                    row.changed = true;
                    entityVM.propertiesVM.changed = true;
                    entityVM.changed = true;
                }
            });

            // sort properties by order defined in propertyDefinitions for each individual entity
            entityVM.propertiesVM.rowsVM.sort((a, b) => a.propertyDefinition.order - b.propertyDefinition.order);
        }

        // Tiers
        // -----------------------------------------------------
        if (entityCompared.merged?.mergedTiers) {

            entityVM.tiersVM = {
                rowsVM: [],
                changed: false,
            };

            Object.entries(entityCompared.merged.mergedTiers).forEach(([key, mergedTier], index) => {

                const propertyName = "Tier Level - " + (mergedTier.merged?.bcbs_name ?? mergedTier.onOrigin?.bcbs_name ?? mergedTier.onTarget?.bcbs_name ?? key);

                const row: IRowVM = {
                    propertyDefinition: {
                        name: propertyName,
                        logicalName: key,
                        order: index,
                    },
                    origin: mergedTier.onOrigin?.name,
                    target: mergedTier.onTarget?.name,
                    changed: false,
                    parent: entityVM,
                    mergeAction: EMergeVMStatus.Disabled,
                };

                entityVM.tiersVM!.rowsVM.push(row);

                // change detection: 
                if (mergedTier.compareStatus !== ECompareStatus.Identical) {
                    row.changed = true;
                    entityVM.tiersVM!.changed = true;
                    entityVM.changed = true;
                }
            });

            // entityVM.tiersVM.rowsVM.sort((a, b) => {
            //     return a.propertyDefinition.name.localeCompare(b.propertyDefinition.name);
            // });
        }

        // References
        // -----------------------------------------------------
        if (entityCompared.merged?.mergedReferences) {

            const compareScope = entityCompared.context?.[entityCompared.context.length - 1]?.name;

            let referenceLabel = "Reference";

            switch (compareScope) {
                case EModelContext.benefits:
                    referenceLabel = "Plan";
                    break;
                case EModelContext.plans:
                    referenceLabel = "Benefit";
                    break;
            }

            entityVM.referencesVM = {
                rowsVM: [],
                changed: false,
            };

            Object.entries(entityCompared.merged.mergedReferences).forEach(([key, mergedReference], index) => {

                const row: IRowVM = {
                    propertyDefinition: {
                        name: `${referenceLabel} - ` + (mergedReference.onTarget ?? mergedReference.onOrigin ?? key),
                        logicalName: key,
                        order: index,
                    },
                    origin: mergedReference.onOrigin,
                    target: mergedReference.onTarget,
                    changed: false,
                    parent: entityVM,
                    mergeAction: EMergeVMStatus.Disabled,
                };

                entityVM.referencesVM!.rowsVM.push(row);

                // Change Detection:
                if (mergedReference.compareStatus !== ECompareStatus.Identical) {
                    row.changed = true;
                    entityVM.referencesVM!.changed = true;
                    entityVM.changed = true;
                }
            });

            entityVM.referencesVM.rowsVM.sort((a, b) => {
                return a.propertyDefinition.name.localeCompare(b.propertyDefinition.name);
            });
        }

        // Flexible Attribute Model - FAM (Tabs + Sections + Rows + Fields + Attribute Sets + Non-Standards)
        // -----------------------------------------------------
        entityVM.formDefinition.tabs.forEach((tabDefinition, eachTabIndex) => {

            const tabVM: ITabVM = {
                tabDefinition: tabDefinition,
                sectionsVM: [],
                changed: false,
            };

            // Sections:
            tabDefinition.sections.forEach(eachSectionDefinition => {

                const sectionVM: ISectionVM = {
                    sectionDefinition: eachSectionDefinition,
                    attributesVM: [],
                    attributeSetsVM: [],
                    nonStandardsVM: [],
                    changed: false,
                };

                // Sections > Rows :
                eachSectionDefinition.rows.forEach(rowDef => {

                    Object.entries(rowDef.uniqueMasterAttributes).forEach(([key, attributeDefinitionList], index) => {

                        // for now, one master attribute and multiple child attributes, we will always take the first attribute definition,
                        // since they are all the same master attribute, and we only need one of them to render the field in the UI    
                        const attributeDefinition = attributeDefinitionList[0];

                        // TODO any discrepancy between children of given master attribute, to make sure (targetedClaims, ...) are all the same, if not, we need to log a warning and skip the attribute
                        // if (attributeDefinitionList.length > 1) {
                        //     console.warn(`Multiple attribute definitions found for master attribute "${attributeDefinition.bcbs_config_masterattribute.bcbs_name}" (${attributeDefinition.bcbs_config_masterattribute.bcbs_config_masterattributeid}). Using the first one for rendering.`);
                        //     let uniqueIDCards: any;
                        //     attributeDefinitionList.forEach((eachAttributeDefinition, eachIndex) => {
                        //     });
                        // }

                        // apply filters if any, if the attribute does not meet the filter criteria, skip it and do not render it in the UI
                        let skipAttribute = false;

                        skipAttribute = ShouldSkipAttribute(compareConfiguration, attributeDefinition.bcbs_config_attribute);

                        if (skipAttribute) {
                            console.log(`Skipping attribute`, attributeDefinition.bcbs_config_attribute.bcbs_name, attributeDefinition.bcbs_config_attribute.bcbs_config_attributeid);
                            return;
                        }

                        const isAttributeSet = attributeDefinition.bcbs_config_masterattribute.bcbs_attributetypecode === EControlType.attributeSet;

                        // Attributes
                        // -----------------------------------------------------
                        if (!isAttributeSet) {

                            const mKey = attributeDefinition.bcbs_config_attribute._bcbs_config_masterattributeid_value!;

                            // lets find the field, if it exists, in the mergedFields of the entity being compared, using the master attribute id as the key
                            const mergedField = entityCompared.merged?.mergedFields?.[mKey];

                            const attributeVM: IFlexibleFieldVM = {
                                key: key,
                                attributeDefinition: attributeDefinition,
                                fieldVM: mergedField,
                                label: attributeDefinition.bcbs_config_attribute.bcbs_label ?? "?",
                                changed: false,
                                mergeAction: EMergeVMStatus.Disabled,
                                parent: entityVM,
                            };

                            sectionVM.attributesVM.push(attributeVM);

                            if (entityVM._debugAttributesVM)
                                entityVM._debugAttributesVM.push(attributeVM);

                            // TODO moved this so the fields get rendered even if they are not found in the comparison, so we can see them in the UI and know they are missing
                            if (!attributeVM.fieldVM) {
                                // console.warn(`Field for attribute "${attributeDefinition.bcbs_config_attribute.bcbs_name}/${attributeDefinition.bcbs_config_attribute.bcbs_config_attributeid}" not found. Skipping field.`);
                                return;
                            }

                            // fieldVM.compareStatus = mergedField.compareStatus;

                            // Status updates
                            // -----------------------------------------------------
                            if (mergedField && mergedField.merged) {
                                Object.entries(mergedField.merged.mergedValues).forEach(([key, eachValue]) => {
                                    if (eachValue && eachValue.compareStatus !== ECompareStatus.Identical) {
                                        attributeVM.changed = true;
                                        sectionVM.changed = true;
                                        tabVM.changed = true;
                                        entityVM.changed = true;
                                    }
                                });
                            }


                            // if ((field.onOrigin?.visibility && field.onTarget?.visibility) ||
                            //     (!field.onOrigin && field.onTarget?.visibility) ||
                            //     (field.onOrigin?.visibility && !field.onTarget)
                            // )
                            //     return;
                        }

                        // Attribute Set
                        // -----------------------------------------------------
                        if (isAttributeSet) {
                            // if it's an attribute set, we need to render it differently,
                            // we need to render the attribute set as a container, and then render the attributes inside the attribute set,
                            // we also need to render the properties of the attribute set

                            const attributeSetsByAttributeRefId = entityCompared.merged?.mergedAttributeSetsByRefId?.[attributeDefinition.bcbs_config_masterattribute.bcbs_config_masterattributeid!] ?? [];

                            // TODO zzzzzzzzzzzzzzzzzz get the options for this injected, for now reading from sate
                            const attributeSetForm = config.cache.newAttributeSetFormDefinitions[attributeDefinition.bcbs_config_masterattribute.bcbs_config_masterattributeid!];

                            const attributeSetMasterVM: IAttributeSetFormVM = {
                                formDefinition: attributeSetForm,
                                attributeSets: [],
                            }

                            sectionVM.attributeSetsVM.push(attributeSetMasterVM);

                            attributeSetsByAttributeRefId.forEach((eachAttributeSet, index) => {

                                const nAttributeSetVM: IAttributeSetVM = {
                                    id: eachAttributeSet.merged?.key ?? Math.random().toString(),
                                    name: eachAttributeSet.onOrigin?.name ?? eachAttributeSet.onTarget?.name ?? "?",
                                    friendlyName: eachAttributeSet.onOrigin?.name ?? eachAttributeSet.onTarget?.name ?? "?",
                                    definition: attributeDefinition,
                                    tabDefinition: tabDefinition,
                                    sectionDefinition: eachSectionDefinition,
                                    formDefinition: attributeSetForm,
                                    changed: false,
                                    propertiesVM: {
                                        rowsVM: [],
                                        changed: false,
                                    },
                                    attributesVM: [],
                                    compareResult: ECompareVMStatus.Unknown,
                                    mergeAction: EMergeVMStatus.Disabled,
                                    parent: entityVM,
                                    type: ESetVMType.attributeSet,
                                };

                                attributeSetMasterVM.attributeSets.push(nAttributeSetVM);

                                if (entityVM._debugAttributeSetVM)
                                    entityVM._debugAttributeSetVM.push(nAttributeSetVM);

                                // Attribute Set - Properties
                                // -----------------------------------------------------
                                Object.entries(eachAttributeSet?.merged?.mergedProperties ?? {}).forEach(([key, eachMergedProperty]) => {

                                    const propertyDefinition = propertyDefinitions[key] ?? {
                                        name: key,
                                        logicalName: key,
                                        order: 999,
                                    };

                                    const property: IRowVM = {
                                        propertyDefinition: propertyDefinition,
                                        origin: eachMergedProperty.onOrigin,
                                        target: eachMergedProperty.onTarget,
                                        changed: false,
                                        parent: nAttributeSetVM,
                                        mergeAction: EMergeVMStatus.Disabled,
                                    };

                                    if (property.origin !== property.target) {
                                        property.changed = true;
                                        nAttributeSetVM.changed = true;
                                        sectionVM.changed = true;
                                        tabVM.changed = true;
                                        entityVM.changed = true;
                                    }

                                    nAttributeSetVM.propertiesVM.rowsVM.push(property);
                                });

                                // Attribute Set - Flexible Attributes (Tabs + Sections + Rows + Fields)
                                // -----------------------------------------------------
                                const fields = eachAttributeSet.merged?.mergedFields ?? {};

                                attributeSetForm.tabs.forEach((attributeSetTabForm, eachTabIndex) => {
                                    attributeSetTabForm.sections.forEach(attributeSetSectionForm => {
                                        attributeSetSectionForm.rows.forEach(attributeSetRowForm => {
                                            Object.entries(attributeSetRowForm.uniqueMasterAttributes).forEach(([key, attributeDefinitionList], index) => {
                                                // grab the first element of uniqueMasterAttributes
                                                const firstAttributeDefinition = attributeDefinitionList[0];

                                                const masterAttributeId = firstAttributeDefinition.bcbs_config_attribute._bcbs_config_masterattributeid_value!;

                                                const field = fields[masterAttributeId];

                                                if (!field)
                                                    return;

                                                const fieldVM: IFlexibleFieldVM = {
                                                    key: firstAttributeDefinition.bcbs_config_attribute.bcbs_config_attributeid + "_" + masterAttributeId,
                                                    label: firstAttributeDefinition.bcbs_config_attribute.bcbs_label ?? "?",
                                                    attributeDefinition: firstAttributeDefinition,
                                                    fieldVM: field,
                                                    changed: false,
                                                    mergeAction: EMergeVMStatus.Disabled,
                                                    parent: nAttributeSetVM,
                                                };

                                                nAttributeSetVM.attributesVM.push(fieldVM);

                                                if (field.merged) {
                                                    Object.entries(field.merged.mergedValues).forEach(([_, eachFieldValue]) => {
                                                        if (eachFieldValue?.compareStatus !== ECompareStatus.Identical) {
                                                            fieldVM.changed = true;
                                                            nAttributeSetVM.changed = true;
                                                            sectionVM.changed = true;
                                                            tabVM.changed = true;
                                                            entityVM.changed = true;
                                                        }
                                                    });
                                                }

                                                // if ((field.onOrigin?.visibility && field.onTarget?.visibility) ||
                                                //     (!field.onOrigin && field.onTarget?.visibility) ||
                                                //     (field.onOrigin?.visibility && !field.onTarget)
                                                // )
                                                //     return;
                                            })
                                        })
                                    })
                                });

                                // Status
                                // -----------------------------------------------------
                                // Determine the status of the attribute set based on whether it exists in origin and/or target
                                // and whether it has changes
                                if (eachAttributeSet.onOrigin && eachAttributeSet.onTarget) {
                                    if (nAttributeSetVM.changed) {
                                        nAttributeSetVM.compareResult = ECompareVMStatus.Modified;
                                    } else {
                                        nAttributeSetVM.compareResult = ECompareVMStatus.Identical;
                                    }
                                } else if (eachAttributeSet.onOrigin && !eachAttributeSet.onTarget) {
                                    nAttributeSetVM.compareResult = ECompareVMStatus.OnOriginOnly;
                                } else if (!eachAttributeSet.onOrigin && eachAttributeSet.onTarget) {
                                    nAttributeSetVM.compareResult = ECompareVMStatus.OnTargetOnly;
                                } else {
                                    throw new Error("Unexpected compare status: " + entityCompared.compareStatus);
                                }

                                // Merge Action (attribute set)
                                // -----------------------------------------------------
                                // Determine the default merge action for the attribute set based on its status,
                                // for example if the attribute set is only in origin, the default merge action would be to keep origin,
                                // if it's only in target, the default merge action would be to keep target,
                                // if it's modified, the default merge action could be to keep both or to show a diff,
                                // this is something that can be configured based on the requirements
                                nAttributeSetVM.mergeAction = EMergeVMStatus.UseTarget;

                            })

                        }
                    });

                })

                // Non-Standard Section
                // -----------------------------------------------------
                // Non-standard sections are sections that are not defined in the flexible form definition, but are present in the data,
                // these sections are rendered at the end of the sections list, and are used to render any non-standard attributes
                // that are not part of the flexible form definition but are present in the data,
                // and also to allow for flexibility in the data model without having to update the flexible form definition
                // every time there is a change in the data model
                {
                    const sectionId = eachSectionDefinition.section.bcbs_config_sectionid!;

                    // TODO get target CACHE
                    const nonStandardForm = config.cache.newNonStandardFormDefinitions[sectionId];

                    if (nonStandardForm) {

                        const nonStandardsBySectionRef = entityCompared.merged?.mergedNonStandardsBySectionRef ?? {};
                        const sectionNonStandards = nonStandardsBySectionRef[sectionId];

                        if (sectionNonStandards) {

                            Object.entries(sectionNonStandards).forEach(([_, sectionNonStandard]) => {
                                if (!sectionNonStandards)
                                    return;

                                const nNonStandardVM: INonStandardVM = {
                                    id: sectionNonStandard.merged?.key ?? Math.random().toString(),
                                    name: sectionNonStandard.onOrigin?.name ?? sectionNonStandard.onTarget?.name ?? "?",
                                    friendlyName: sectionNonStandard.onOrigin?.name ?? sectionNonStandard.onTarget?.name ?? "?",
                                    tabDefinition: tabDefinition,
                                    sectionDefinition: eachSectionDefinition,
                                    formDefinition: nonStandardForm,
                                    changed: false,
                                    propertiesVM: {
                                        rowsVM: [],
                                        changed: false,
                                    },
                                    attributesVM: [],
                                    compareResult: ECompareVMStatus.Unknown,
                                    mergeAction: EMergeVMStatus.UseTarget,
                                    parent: entityVM,
                                    type: ESetVMType.nonStandard,
                                };

                                sectionVM.nonStandardsVM.push(nNonStandardVM);


                                if (entityVM._debugNonStandardVM)
                                    entityVM._debugNonStandardVM.push(nNonStandardVM);

                                // Nonstandard - Properties
                                // --------------------------
                                Object.entries(sectionNonStandard?.merged?.mergedProperties ?? {}).forEach(([key, eachMergedProperty]) => {
                                    const propertyDefinition = this.NonStandardPropertyDefinition[key] ?? propertyDefinitions[key] ?? {
                                        name: key,
                                        logicalName: key,
                                        order: 999,
                                    };

                                    const property: IRowVM = {
                                        propertyDefinition,
                                        origin: eachMergedProperty.onOrigin,
                                        target: eachMergedProperty.onTarget,
                                        changed: false,
                                        parent: nNonStandardVM,
                                        mergeAction: EMergeVMStatus.Disabled,
                                    };

                                    if (property.origin !== property.target) {
                                        property.changed = true;
                                        nNonStandardVM.changed = true;
                                        sectionVM.changed = true;
                                        tabVM.changed = true;
                                        entityVM.changed = true;
                                    }

                                    nNonStandardVM.propertiesVM.rowsVM.push(property);
                                });

                                // Nonstandard - Flexible Attributes (Tabs + Sections + Rows + Fields)
                                const nonStandardFields = sectionNonStandard.merged?.mergedFields ?? {};

                                nonStandardForm.tabs.forEach(nonStandardTabForm => {
                                    nonStandardTabForm.sections.forEach(nonStandardSectionForm => {
                                        nonStandardSectionForm.rows.forEach(nonStandardRowForm => {
                                            Object.entries(nonStandardRowForm.uniqueMasterAttributes).forEach(([_, attributeDefinitionList]) => {

                                                const firstAttributeDefinition = attributeDefinitionList[0];

                                                const masterAttributeId = firstAttributeDefinition.bcbs_config_attribute._bcbs_config_masterattributeid_value!;

                                                const field = nonStandardFields[masterAttributeId];

                                                if (!field)
                                                    return;

                                                const fieldVM: IFlexibleFieldVM = {
                                                    key: firstAttributeDefinition.bcbs_config_attribute.bcbs_config_attributeid + "_" + masterAttributeId,
                                                    label: firstAttributeDefinition.bcbs_config_attribute.bcbs_label ?? "?",
                                                    attributeDefinition: firstAttributeDefinition,
                                                    fieldVM: field,
                                                    changed: false,
                                                    parent: nNonStandardVM,
                                                    mergeAction: EMergeVMStatus.Disabled,
                                                };

                                                nNonStandardVM.attributesVM.push(fieldVM);

                                                if (field.merged) {
                                                    Object.entries(field.merged.mergedValues).forEach(([_, eachFieldValue]) => {
                                                        if (eachFieldValue?.compareStatus !== ECompareStatus.Identical) {
                                                            fieldVM.changed = true;
                                                            nNonStandardVM.changed = true;
                                                            sectionVM.changed = true;
                                                            tabVM.changed = true;
                                                            entityVM.changed = true;
                                                        }
                                                    })
                                                }

                                            });
                                        });
                                    });
                                });

                                // Status
                                // -----------------------------------------------------
                                // Determine the status of the non-standard section based on whether it exists in origin and/or target, and whether it has changes
                                if (sectionNonStandard.onOrigin && sectionNonStandard.onTarget) {
                                    if (nNonStandardVM.changed) {
                                        nNonStandardVM.compareResult = ECompareVMStatus.Modified;
                                    }
                                    else {
                                        nNonStandardVM.compareResult = ECompareVMStatus.Identical;
                                    }
                                }
                                else if (sectionNonStandard.onOrigin && !sectionNonStandard.onTarget)
                                    nNonStandardVM.compareResult = ECompareVMStatus.OnOriginOnly;
                                else if (!sectionNonStandard.onOrigin && sectionNonStandard.onTarget)
                                    nNonStandardVM.compareResult = ECompareVMStatus.OnTargetOnly;
                                else {
                                    throw new Error("Unexpected compare status: " + entityCompared.compareStatus);
                                }


                                // TODO section change detection

                                // Merge Action (Nonstandard)
                                // -----------------------------------------------------
                                // calculate the default merge action based on the compare status,
                                // this will be used to determine which side to default to when there is a difference
                                nNonStandardVM.mergeAction = EMergeVMStatus.UseTarget;

                            });
                        }
                    }
                }

                // only push sections that have rows or attribute sets, this way we don't render empty sections in the UI
                if (sectionVM.attributesVM.length > 0 || sectionVM.attributeSetsVM.length > 0 || sectionVM.nonStandardsVM.length > 0) {
                    tabVM.sectionsVM.push(sectionVM);
                }

            })

            // only push tabs that have sections, this way we don't render empty tabs in the UI
            if (tabVM.sectionsVM.length > 0) {
                tabs.push(tabVM);
            }
        });

        // Status
        // -----------------------------------------------------
        // Determine the status of the entity based on whether it exists in origin and/or target, and whether it has changes
        if (entityCompared.compareStatus === ECompareStatus.Identical) {
            if (entityVM.changed) {
                entityVM.compareResult = ECompareVMStatus.Modified;
            }
            else {
                entityVM.compareResult = ECompareVMStatus.Identical;
            }
        } else if (entityCompared.compareStatus === ECompareStatus.OnOriginOnly) {
            entityVM.compareResult = ECompareVMStatus.OnOriginOnly;
        } else if (entityCompared.compareStatus === ECompareStatus.OnTargetOnly) {
            entityVM.compareResult = ECompareVMStatus.OnTargetOnly;
        } else {
            throw new Error("Unexpected compare status: " + entityCompared.compareStatus);
        }

        // Merge Action
        // -----------------------------------------------------
        // calculate the default merge action based on the compare status,
        // this will be used to determine which side to default to when there is a difference
        const mergeAction = determineOptimalMergeAction(
            entityVM.compareResult,
            mergeUserPreferredOption,
            undefined,
            context
        );

        // Cascade Merge Action for child entities
        // -----------------------------------------------------
        CascadeChildMergeActions(
            mergeAction,
            mergeUserPreferredOption,
            entityVM,
            null,
            null,
        );

        return entityVM;
    }

    public static async GeneratePrimePharmacyConfigurationModel(
        nOrigin: IPrimeData,
        nTarget: IPrimeData,
        primeCategory: bcbs_config_category,
        primeBenefitId: string,
        config: FAM_IGroupConfigurationConfig,
    ): Promise<IPrimePharmacyComparisonVM> {

        console.log('Comparison Engine - Origin:', nOrigin);
        console.log('Comparison Engine - Target:', nTarget);
        console.log('Comparison Engine - Prime Benefit ID:', primeBenefitId);
        console.log('Comparison Engine - Config:', config);

        const comparisonEngine = new ComparisonEngine(nOrigin.state, nTarget.state);
        const comparisonResult = await comparisonEngine.processAsync();

        const primeBenefit = comparisonResult.Benefits[primeBenefitId];

        // Properties
        // -------------------------------------------------------------------
        const summary = (() => {
            return ComparisonViewModelGenerator.GeneratePropertiesModel(
                nOrigin?.summary ?? {} as any,
                nTarget?.summary ?? {} as any,
                () => ComparisonViewModelGenerator.PrimePharmacyPropertyDefinition
            );
        })();

        // Benefit
        // -------------------------------------------------------------------
        // Create a new flexible form definition for prime
        // shallow copy the original and add the medical fields where it needs to go
        const prime = await ComparisonViewModelGenerator.GeneratePrimeModel(primeBenefit, primeCategory, config);

        // Group Numbers
        // -------------------------------------------------------------------
        const groupNumbers = await ComparisonViewModelGenerator.GenerateGroupNumberModel(comparisonResult.GroupNumbers, config);

        const compareViewModel: IPrimePharmacyComparisonVM = {
            summary,
            prime,
            groupNumbers,
        };

        console.log('Comparison Engine:', comparisonEngine);
        console.log('Compare View Model:', compareViewModel);

        return compareViewModel;

    }

    public static async GenerateGroupNumberModel(
        groupNumber: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>>,
        config: FAM_IGroupConfigurationConfig,
    ): Promise<IEntitiesContainerVM> {

        const groupNumberCategory = config.cache?.map_bcbs_config_category_named['Group Number'];
        const flexibleFormDefinition = config.cache.categoryFormDefinitions[groupNumberCategory.bcbs_config_categoryid!];

        const groupNumberVM: IEntitiesContainerVM = {
            entities: [],
            changed: false,
            title: modelContextToFriendlyName[EModelContext.groupNumbers],
            context: EModelContext.groupNumbers,
        };

        Object.entries(groupNumber).forEach(([key, eachGroupNumber]) => {

            const compareVM = ComparisonViewModelGenerator.GenerateCategoryModel(
                flexibleFormDefinition,
                eachGroupNumber,
                config,
                () => ComparisonViewModelGenerator.GroupNumberPropertyDefinitions,
                null,
                null,
                EModelContext.groupNumbers,
            );

            groupNumberVM.entities.push(compareVM);

            if (compareVM.changed)
                groupNumberVM.changed = true;
        })

        return groupNumberVM;
    }

    public static async GeneratePrimeModel(
        primeBenefit: IComparable<IDataEntityLike, IDataEntityLikeMerged>,
        primeCategory: bcbs_config_category,
        config: FAM_IGroupConfigurationConfig,
    ) {

        let primeFlexibleFormDefinition = config.cache.categoryFormDefinitions[primeCategory.bcbs_config_categoryid!];

        primeFlexibleFormDefinition = InjectIntoFlexibleFormDef(primeFlexibleFormDefinition, primeCategory);

        const compareVM = ComparisonViewModelGenerator.GenerateCategoryModel(
            primeFlexibleFormDefinition,
            primeBenefit,
            config,
            () => ComparisonViewModelGenerator.BenefitPropertyDefinition,
            null,
            null,
            EModelContext.benefits,
        );

        return compareVM;
    }

    public static async GenerateGroupConfigurationModel(
        originState: IGCDataLight,
        targetState: IGCDataLight,
        comparisonEngine: ComparisonEngine,
        config: FAM_IGroupConfigurationConfig,
        compareConfiguration: ICompareConfiguration,
    ): Promise<IGroupConfigurationComparisonVM> {

        // Scope Methods
        // -----------------------------------------------------
        function blankGroupConfigurationModel(): IGroupConfigurationComparisonVM {
            return {
                summary: {
                    rowsVM: [],
                    changed: false,
                },
                groupConfiguration: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.groupConfiguration],
                    context: EModelContext.groupConfiguration,
                },
                plan: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.plans],
                    context: EModelContext.plans,
                },
                benefit: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.benefits],
                    context: EModelContext.benefits,
                },
                feesSummary: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.feesSummary],
                    context: EModelContext.feesSummary,
                },
                stopLossSummary: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.stopLossSummary],
                    context: EModelContext.stopLossSummary,
                },
                groupNumber: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.groupNumbers],
                    context: EModelContext.groupNumbers,
                },
                cesNumber: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.cesNumbers],
                    context: EModelContext.cesNumbers,
                },
                department: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.departments],
                    context: EModelContext.departments,
                },
                nonStandardTemplate: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.nonStandardTemplates],
                    context: EModelContext.nonStandardTemplates,
                },
                feesAndPremium: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.feesAndPremiums],
                    context: EModelContext.feesAndPremiums,
                },
                stopLoss: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.stopLoss],
                    context: EModelContext.stopLoss,
                },
                accountsReceivable: {
                    entities: [],
                    changed: false,
                    title: modelContextToFriendlyName[EModelContext.accountsReceivable],
                    context: EModelContext.accountsReceivable,
                },
            };
        }

        function ExtractSummaryProperties(
            state: IGCDataLight
        ) {

            const snapshot = state.snapshot as Ibcbs_groupconfigurationsnapshot_extended;
            const json = state.json;
            // const account = state.account;

            const summary: Record<keyof typeof ComparisonViewModelGenerator.ConfigurationSummaryPropertyDefinition, string | undefined> = {

                snapshot_name: snapshot.bcbs_name,
                snapshot_effectiveDate: snapshot.bcbs_effectivedate
                    ? formatDate(parseDate(snapshot.bcbs_effectivedate!, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')
                    : undefined,
                snapshot_fundingType: snapshot.bcbs_fundingtypecode
                    ? snapshot['bcbs_fundingtypecode@OData.Community.Display.V1.FormattedValue']
                    : undefined,
                lineOfBusiness: json.lineOfBusiness
                    ? json.lineOfBusiness.name
                    : undefined,
                erisa: json.erisa
                    ? json.erisa.name
                    : undefined,
                snapshot_changeType: snapshot.bcbs_changetypecode !== undefined
                    ? snapshot['bcbs_changetypecode@OData.Community.Display.V1.FormattedValue'] ?? undefined
                    : undefined,
                snapshot_dualFunded: snapshot.bcbs_dualfunded !== undefined
                    ? (snapshot.bcbs_dualfunded ? "Yes" : "No")
                    : undefined,
                grandFathered: json.grandFathered !== undefined
                    ? json.grandFathered?.name
                    : undefined,
                snapshot_mnil: snapshot.bcbs_ismnil !== undefined
                    ? (snapshot.bcbs_ismnil ? "Yes" : "No")
                    : undefined,
                snapshot_reconciliation: snapshot.bcbs_isreconciliation !== undefined
                    ? (snapshot.bcbs_isreconciliation ? "Yes" : "No")
                    : undefined,
                opportunity: json.opportunity
                    ? json.opportunity.name
                    : undefined,
                snapshot_statuscode: snapshot.statuscode
                    ? bcbs_groupconfigurationsnapshot.statuscode[snapshot.statuscode as bcbs_groupconfigurationsnapshot.Tstatuscode].name
                    : undefined,
                snapshot_createdon: snapshot.createdon
                    ? formatDate(parseDate(snapshot.createdon!, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')
                    : undefined,
                snapshot_createdby: snapshot._createdby_value
                    ? snapshot['_createdby_value@OData.Community.Display.V1.FormattedValue']
                    : undefined,
                snapshot_modifiedon: snapshot.modifiedon
                    ? formatDate(parseDate(snapshot.modifiedon!, 'yyyy-MM-ddThh:mm:ssZ as local'), 'local:MM/dd/yyyy')
                    : undefined,
                snapshot_modifiedby: snapshot._modifiedby_value
                    ? snapshot['_modifiedby_value@OData.Community.Display.V1.FormattedValue']
                    : undefined,
                snapshot_bcbs_originatingsnapshotid_name: snapshot._bcbs_originatingsnapshotid_value
                    ? snapshot['_bcbs_originatingsnapshotid_value@OData.Community.Display.V1.FormattedValue']
                    : undefined,
                snapshot_bcbs_originatingsnapshot2id_name: snapshot._bcbs_originatingsnapshot2id_value
                    ? snapshot['_bcbs_originatingsnapshot2id_value@OData.Community.Display.V1.FormattedValue']
                    : undefined,
                snapshot_releaseid_name: snapshot._bcbs_releaseid_value
                    ? snapshot['_bcbs_releaseid_value@OData.Community.Display.V1.FormattedValue']
                    : undefined,
            }

            return summary;
        }

        async function createModel(
            list: Record<string, IComparable<IDataEntityLike, IDataEntityLikeMerged>>,
            title: string,
            flexibleFormDefinition: IFlexibleFormCategoryDefinition | ((eachEntity: IComparable<IDataEntityLike, IDataEntityLikeMerged>) => IFlexibleFormCategoryDefinition),
            propertyDefinitions: () => Record<string, IPropertyDefinition>,
            context: EModelContext
        ): Promise<IEntitiesContainerVM> {

            const isFlexibleFormDefinitionFunction = typeof flexibleFormDefinition === "function";

            const entityVM: IEntitiesContainerVM = {
                entities: [],
                changed: false,
                title: title,
                context: context,
            };

            return await new Promise<IEntitiesContainerVM>(
                (resolve) => setImmediate(() => {

                    Object.entries(list)
                        .forEach(([key, eachEntity]) => {

                            const formDefinition = isFlexibleFormDefinitionFunction
                                ? flexibleFormDefinition(eachEntity)
                                : flexibleFormDefinition;

                            const compareVM = ComparisonViewModelGenerator.GenerateCategoryModel(
                                formDefinition,
                                eachEntity,
                                config,
                                propertyDefinitions,
                                compareConfiguration,
                                compareConfiguration?.defaultMergeAction,
                                context,
                            );

                            entityVM.entities.push(compareVM);

                            if (compareVM.changed)
                                entityVM.changed = true;
                        });

                    resolve(entityVM);
                })
            );
        }

        const model = blankGroupConfigurationModel()

        // summary
        // -----------------------------------------------------
        model.summary = (() => {

            const nOrigin = originState.snapshot
                ? ExtractSummaryProperties(originState)
                : null;

            const nTarget = targetState.snapshot
                ? ExtractSummaryProperties(targetState)
                : null;

            return ComparisonViewModelGenerator.GeneratePropertiesModel(
                nOrigin ?? {} as any,
                nTarget ?? {} as any,
                () => ComparisonViewModelGenerator.ConfigurationSummaryPropertyDefinition
            );
        })();

        // group configuration
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named['Group Configuration'];
            const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];
            const propertyDefinitions = () => ComparisonViewModelGenerator.GroupConfigurationPropertyDefinition;
            model.groupConfiguration = await createModel(
                comparisonEngine.groupConfiguration,
                "Group Configuration",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.groupConfiguration
            );
            model.groupConfiguration.entities[0].friendlyName = "Group Configuration";
        }

        // plans
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named.Plan;
            const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];
            const propertyDefinitions = () => ComparisonViewModelGenerator.PlanPropertyDefinition;

            model.plan = await createModel(
                comparisonEngine.plans,
                "Plans",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.plans
            );
        }

        // Benefit
        // -----------------------------------------------------
        {
            const getFlexibleFormDefinitionForBenefitCategory = (eachEntity: IComparable<IDataEntityLike, IDataEntityLikeMerged>) => {
                const eachEn = (eachEntity.onTarget ?? eachEntity.onOrigin) as FAM_IBenefit;

                const category = config.cache?.map_bcbs_config_category[eachEn.bcbs_config_categoryid!];
                const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];

                return flexibleFormDefinition;
            }

            const propertyDefinitions = () => ComparisonViewModelGenerator.BenefitPropertyDefinition;

            model.benefit = await createModel(
                comparisonEngine.benefits,
                "Benefits",
                getFlexibleFormDefinitionForBenefitCategory,
                propertyDefinitions,
                EModelContext.benefits
            );
        }

        // Group Numbers
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named['Group Number'];
            const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];
            const propertyDefinitions = () => ComparisonViewModelGenerator.GroupNumberPropertyDefinitions;
            model.groupNumber = await createModel(
                comparisonEngine.groupNumbers,
                "Group Numbers",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.groupNumbers
            );
        }

        // CES Numbers
        // -----------------------------------------------------
        {
            const blankFlexDef: IFlexibleFormCategoryDefinition = {
                category: {} as any,
                tabs: [],
                attributes: [],
            };
            const propertyDefinitions = () => ComparisonViewModelGenerator.CESNumberPropertyDefinitions;
            model.cesNumber = await createModel(
                comparisonEngine.cesNumbers,
                "CES Numbers",
                blankFlexDef,
                propertyDefinitions,
                EModelContext.cesNumbers
            );
        }

        // Departments
        // -----------------------------------------------------
        {
            const blankFlexDef: IFlexibleFormCategoryDefinition = {
                category: {} as any,
                tabs: [],
                attributes: [],
            };
            const propertyDefinitions = () => ComparisonViewModelGenerator.DepartmentPropertyDefinitions;
            model.department = await createModel(
                comparisonEngine.departments,
                "Departments",
                blankFlexDef,
                propertyDefinitions,
                EModelContext.departments
            );
        }


        // Fees and Premiums
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named['Fees and Premiums'];
            const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];
            const propertyDefinitions = () => ComparisonViewModelGenerator.FeesAndPremiumPropertyDefinition;
            model.feesAndPremium = await createModel(
                comparisonEngine.feesAndPremiums,
                "Fees and Premiums",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.feesAndPremiums
            );
        }

        // Fees Summary
        // -----------------------------------------------------
        {
            const blankFlexDef: IFlexibleFormCategoryDefinition = { category: {} as any, tabs: [], attributes: [] };
            const propertyDefinitions = () => ComparisonViewModelGenerator.FeesSummaryPropertyDefinition;
            model.feesSummary = await createModel(
                comparisonEngine.feesSummary,
                "Fees Summary",
                blankFlexDef,
                propertyDefinitions,
                EModelContext.feesSummary
            );

            // fees summary is a special case where we want to disable merging at
            // the row level and only allow merging at the entity level,
            // this is because fees summary is a rollup of fees and premium
            // and allowing merging at the row level would cause confusion as
            // to which fee and premium records are being merged, so we set
            // the merge action of each row to ENotSupported to disable
            // merging at the row level
            model.feesSummary.entities.forEach(entity => {
                entity.mergeAction = EMergeVMStatus.Disabled;
            });
        }

        // Stop Loss Summary
        // -----------------------------------------------------
        {
            const blankFlexDef: IFlexibleFormCategoryDefinition = { category: {} as any, tabs: [], attributes: [] };
            const propertyDefinitions = () => ComparisonViewModelGenerator.StopLossSummaryPropertyDefinition;
            model.stopLossSummary = await createModel(
                comparisonEngine.stopLossSummary,
                "Stop Loss Summary",
                blankFlexDef,
                propertyDefinitions,
                EModelContext.stopLossSummary,
            );

            // stop loss summary is a special case where we want to disable merging at
            // the row level and only allow merging at the entity level,
            // this is because stop loss summary is a rollup of stop loss
            // and allowing merging at the row level would cause confusion as
            // to which stop loss records are being merged, so we set
            // the merge action of each row to ENotSupported to disable
            // merging at the row level
            model.stopLossSummary.entities.forEach(entity => {
                entity.mergeAction = EMergeVMStatus.Disabled;
            });
        }

        // Stop Loss
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named['Stop Loss'];
            const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];
            const propertyDefinitions = () => ComparisonViewModelGenerator.StopLossPropertyDefinition;
            model.stopLoss = await createModel(
                comparisonEngine.stopLosses,
                "Stop Loss",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.stopLoss,
            );
        }

        // Accounts Receivable
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named['Accounts Receivable'];
            const flexibleFormDefinition = config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!];
            const propertyDefinitions = () => ComparisonViewModelGenerator.AccountsReceivablePropertyDefinition;
            model.accountsReceivable = await createModel(
                comparisonEngine.accountsReceivables,
                "Accounts Receivable",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.accountsReceivable
            );
        }

        // Non-Standard Templates
        // -----------------------------------------------------
        {
            const category = config.cache?.map_bcbs_config_category_named['Non-Standard Template'];
            const flexibleFormDefinition = category
                ? config.cache.categoryFormDefinitions[category.bcbs_config_categoryid!]
                : {
                    category: {} as any,
                    tabs: [],
                    attributes: [],
                } as IFlexibleFormCategoryDefinition;
            const propertyDefinitions = () => ComparisonViewModelGenerator.NonStandardTemplatePropertyDefinitions;
            model.nonStandardTemplate = await createModel(
                comparisonEngine.nonStandardTemplates,
                "Non-Standard Templates",
                flexibleFormDefinition,
                propertyDefinitions,
                EModelContext.nonStandardTemplates
            );
        }

        return model;

    }


}

interface IBestMergerInstructions {
    isNonStandard?: boolean,
    isAttributeSet?: boolean,
    isGroupConfiguration?: boolean,
    preventMerge?: boolean,
    preventAddition?: boolean,
    preventDeletion?: boolean,
}

const DISABLED_PARENT_MERGE_OPTIONS: EMergeVMStatus[] = [
    EMergeVMStatus.ParentOverride,
    EMergeVMStatus.ParentOverrideUseOrigin,
    EMergeVMStatus.ParentOverrideUseTarget,
];

const NO_MERGE_OPTIONS: EMergeVMStatus[] = [];

const ON_TARGET_ONLY_MERGE_OPTIONS: EMergeVMStatus[] = [
    EMergeVMStatus.RemoveTarget,
    EMergeVMStatus.UseTarget,
    ...DISABLED_PARENT_MERGE_OPTIONS,
];

const ON_ORIGIN_ONLY_MERGE_OPTIONS: EMergeVMStatus[] = [
    EMergeVMStatus.AddOrigin,
    EMergeVMStatus.UseTarget,
    ...DISABLED_PARENT_MERGE_OPTIONS,
];

const MODIFIED_MERGE_OPTIONS: EMergeVMStatus[] = [
    EMergeVMStatus.UseOrigin,
    EMergeVMStatus.Merge,
    EMergeVMStatus.UseTarget,
    ...DISABLED_PARENT_MERGE_OPTIONS,
];

const MODIFIED_MERGE_OPTIONS_NON_STANDARD_OR_ATTRIBUTE_SET: EMergeVMStatus[] = [
    EMergeVMStatus.UseOrigin,
    EMergeVMStatus.UseTarget,
    ...DISABLED_PARENT_MERGE_OPTIONS,
];

const MODIFIED_MERGE_OPTIONS_GROUP_CONFIGURATION: EMergeVMStatus[] = [
    EMergeVMStatus.Merge,
    EMergeVMStatus.UseTarget,
];


const DEFAULT_MERGE_OPTIONS: EMergeVMStatus[] = [EMergeVMStatus.UseTarget];

/**
 * Returns the available merge options based on the status and type of the entity.
 * @param compareVMStatus
 * @param isNonStandard
 * @param isAttributeSet
 * @returns
 */
export const getAvailableMergeOptions = (
    compareVMStatus: ECompareVMStatus,
    options?: IBestMergerInstructions
): EMergeVMStatus[] => {

    switch (compareVMStatus) {
        case ECompareVMStatus.OnTargetOnly:
            return ON_TARGET_ONLY_MERGE_OPTIONS;

        case ECompareVMStatus.OnOriginOnly:
            return ON_ORIGIN_ONLY_MERGE_OPTIONS;

        case ECompareVMStatus.Identical:
            return NO_MERGE_OPTIONS;

        case ECompareVMStatus.Modified:
            if (options?.isNonStandard || options?.isAttributeSet) {
                // If the entity is a non-standard or attribute set, we want to remove the "Merge" option from the available options.
                // we currently do not support merging non-standard or attribute set entities, at attribute level
                return MODIFIED_MERGE_OPTIONS_NON_STANDARD_OR_ATTRIBUTE_SET;
            } else if (options?.isGroupConfiguration) {
                // for Group Configuration, we want to remove the "Use Origin" option from the available options
                // Group Configurations
                return MODIFIED_MERGE_OPTIONS_GROUP_CONFIGURATION;
            } else {

                let ret = MODIFIED_MERGE_OPTIONS;

                if (options?.preventMerge) {
                    ret = ret.filter(action => action !== EMergeVMStatus.Merge);
                }

                return ret;
            }

        case ECompareVMStatus.Unknown:
            return NO_MERGE_OPTIONS;

        default:
            const _exhaustiveCheck: never = compareVMStatus;
            return DEFAULT_MERGE_OPTIONS;

    }
};

const getMergeOptionText = (
    mergeOption: EMergeVMStatus,
    compareVMStatus: ECompareVMStatus,
): string => {
    if (DISABLED_PARENT_MERGE_OPTIONS.includes(mergeOption)) {
        return 'Not Applicable (Following Parent)';
    }

    switch (compareVMStatus) {
        case ECompareVMStatus.OnTargetOnly:
            if (mergeOption === EMergeVMStatus.RemoveTarget)
                return 'Pick Origin (Remove From Target)';
            if (mergeOption === EMergeVMStatus.UseTarget)
                return 'Pick Target (Keep Target Unchanged)';
            break;

        case ECompareVMStatus.OnOriginOnly:
            if (mergeOption === EMergeVMStatus.AddOrigin)
                return 'Pick Origin (Add To Target From Origin)';
            if (mergeOption === EMergeVMStatus.UseTarget)
                return 'Pick Target (Keep Target Unchanged) (No Add)';
            break;

        case ECompareVMStatus.Modified:
            if (mergeOption === EMergeVMStatus.UseOrigin)
                return 'Pick Origin (Replace Target With Origin)';
            if (mergeOption === EMergeVMStatus.Merge)
                return 'Merge (Origin With Target)';
            if (mergeOption === EMergeVMStatus.UseTarget)
                return 'Pick Target (Keep Target Unchanged)';
            break;
    }

    return mergeOption === EMergeVMStatus.UseTarget
        ? 'No Action'
        : (EMergeAction_FriendlyLabels[mergeOption] ?? EMergeAction_DiagnosisLabels[mergeOption]);
};

export const getAvailableMergeOptionsAsDropDown = (
    compareVMStatus: ECompareVMStatus,
    options?: IBestMergerInstructions,
): IDropdownOption<EMergeVMStatus>[] => {

    return getAvailableMergeOptions(compareVMStatus, options).map(action => ({
        key: action,
        text: getMergeOptionText(action, compareVMStatus),
        data: action,
        hidden: DISABLED_PARENT_MERGE_OPTIONS.includes(action) ? true : undefined,
    }));

};

export const determineOptimalMergeAction = (
    compareResult: ECompareVMStatus,
    mergeUserPreferredOption?: EMergeVMStatus | null,
    smart?: boolean | null,
    context?: EModelContext | null,
): EMergeVMStatus => {

    // if (mergeUserPreferredOption && !getAvailableMergeOptions(compareVMStatus).includes(mergeUserPreferredOption)) {
    //     console.warn(`The mergeUserPreferredOption "${mergeUserPreferredOption}" is not a valid option for the compareVMStatus "${compareVMStatus}". Falling back to default behavior.`);
    // }

    if (context === EModelContext.groupConfiguration) {
        if (mergeUserPreferredOption === EMergeVMStatus.UseOrigin) {
            return EMergeVMStatus.Merge;
        } else if (mergeUserPreferredOption === EMergeVMStatus.UseTarget) {
            return EMergeVMStatus.UseTarget;
        }
    }

    if (mergeUserPreferredOption === EMergeVMStatus.Pend) {
        // Pending means explicit user selection is required for actionable differences.
        if (compareResult === ECompareVMStatus.OnTargetOnly ||
            compareResult === ECompareVMStatus.OnOriginOnly ||
            compareResult === ECompareVMStatus.Modified) {
            return EMergeVMStatus.Pend;
        }

        return EMergeVMStatus.UseTarget;
    }

    const instructions: IBestMergerInstructions = {}

    if (smart) {
        if (mergeUserPreferredOption === EMergeVMStatus.UseOrigin) {
            instructions.preventDeletion = false;
        }

        if (mergeUserPreferredOption === EMergeVMStatus.UseTarget) {
            instructions.preventAddition = true;
            instructions.preventDeletion = true;
        }
    }

    switch (compareResult) {
        case ECompareVMStatus.Unknown:
            return EMergeVMStatus.UseTarget;

        case ECompareVMStatus.OnTargetOnly:
            // Valid Options:
            // EMergeAction.None
            // EMergeAction.RemoveTarget
            // EMergeAction.ParentOverride

            if (instructions.preventDeletion === true)
                return EMergeVMStatus.UseTarget;
            else if (instructions.preventDeletion === false)
                return EMergeVMStatus.RemoveTarget;

            return EMergeVMStatus.UseTarget;

        case ECompareVMStatus.OnOriginOnly:
            // Valid Options:
            // EMergeAction.None
            // EMergeAction.AddOrigin
            // EMergeAction.ParentOverride

            if (instructions.preventAddition)
                return EMergeVMStatus.UseTarget;

            return EMergeVMStatus.AddOrigin;

        case ECompareVMStatus.Modified:
            // Valid Options:
            // EMergeAction.None
            // EMergeAction.UseOrigin
            // EMergeAction.Merge
            // EMergeAction.ParentOverride

            // Phase 1
            // for modified entities, we will default to keeping target values
            // (not picking origin values, and not merging)
            // because it's safer to not make any change instead of accidentally picking wrong values
            // from origin. Users can manually choose to pick values from origin if they want to.
            // return EMergeAction.None;

            // Phase 2
            if (mergeUserPreferredOption === EMergeVMStatus.UseOrigin)
                return EMergeVMStatus.UseOrigin;
            if (mergeUserPreferredOption === EMergeVMStatus.UseTarget)
                return EMergeVMStatus.UseTarget;

            return EMergeVMStatus.UseTarget

        case ECompareVMStatus.Identical:
            // Valid Options:
            // EMergeAction.None

            // for identical entities, we will default to keeping target values as well,
            // since there is no change
            return EMergeVMStatus.UseTarget;

        default:
            const _exhaustiveCheck: never = compareResult;
            return EMergeVMStatus.UseTarget;
    }
};

/**
 * Cascades the merge action to the child entities (tabs, sections, attributes, and attributeSets, nonstandards)
 *  of the given compare view model.
 * @param parentMergeAction The merge action to be applied to the child entities.
 * @param forEntityVM The compare view model whose child entities will be updated.
 */
export function CascadeChildMergeActions(
    parentMergeAction: EMergeVMStatus,
    defaultMergeAction: TDefaultMergeOption | null,
    forEntityVM: IEntityVM | null,
    forAttributeSetVM: IAttributeSetVM | null,
    forNonStandardVM: INonStandardVM | null,
) {
    enum EContext {
        FAM,
        NonStandard,
        AttributeSet,
    }

    const childMergeAction = (
        context: EContext,
        entityVM: IEntityVM | null,
        attributeSetVM: IAttributeSetVM | null,
        nonStandardVM: INonStandardVM | null
    ) => {

        if (parentMergeAction === EMergeVMStatus.Pend)
            return EMergeVMStatus.Disabled;

        let retAction: EMergeVMStatus = EMergeVMStatus.UseTarget;
        const childEntity = (attributeSetVM || nonStandardVM);

        switch (parentMergeAction) {

            // parent entity is requesting to merge:
            case EMergeVMStatus.Merge:
                retAction = EMergeVMStatus.UseTarget;

                if (
                    (context === EContext.AttributeSet || context === EContext.NonStandard) &&
                    childEntity
                ) {
                    if (defaultMergeAction === EMergeVMStatus.UseOrigin) {

                        if (childEntity.compareResult === ECompareVMStatus.OnOriginOnly)
                            retAction = EMergeVMStatus.AddOrigin;
                        else if (childEntity.compareResult === ECompareVMStatus.OnTargetOnly)
                            retAction = EMergeVMStatus.RemoveTarget;
                        else if (childEntity.compareResult === ECompareVMStatus.Modified)
                            retAction = EMergeVMStatus.UseOrigin;

                    } else if (defaultMergeAction === EMergeVMStatus.UseTarget) {
                        // nothing to do
                        retAction = EMergeVMStatus.UseTarget;
                    }

                    if (defaultMergeAction === EMergeVMStatus.Pend) {
                        retAction = EMergeVMStatus.Pend;
                        break;
                    }
                }

                if (context === EContext.FAM && entityVM) {
                    if (defaultMergeAction === EMergeVMStatus.UseOrigin)
                        retAction = EMergeVMStatus.UseOrigin
                    else
                        retAction = EMergeVMStatus.UseTarget


                    if (defaultMergeAction === EMergeVMStatus.Pend) {
                        retAction = EMergeVMStatus.Pend;
                        break;
                    }

                }

                break;

            case EMergeVMStatus.UseTarget:
                retAction = EMergeVMStatus.ParentOverride;
                break;

            case EMergeVMStatus.UseOrigin:
                retAction = EMergeVMStatus.ParentOverride;
                break;

            case EMergeVMStatus.AddOrigin:
                retAction = EMergeVMStatus.ParentOverride;
                break;

            case EMergeVMStatus.RemoveTarget:
                retAction = EMergeVMStatus.ParentOverride;
                break;

        }

        return retAction;
    }


    const childMergeAction_COPY = () => {

        let fam_mergeAction: EMergeVMStatus = EMergeVMStatus.ParentOverrideUseTarget as any;
        let attributeSet_mergeAction: EMergeVMStatus = EMergeVMStatus.ParentOverrideUseTarget as any;
        let nonStandard_mergeAction: EMergeVMStatus = EMergeVMStatus.ParentOverrideUseTarget as any;

        switch (parentMergeAction) {

            // parent entity is requesting to merge:
            case EMergeVMStatus.Merge:

                // Attributes:
                fam_mergeAction = EMergeVMStatus.UseTarget;
                nonStandard_mergeAction = EMergeVMStatus.UseTarget;
                attributeSet_mergeAction = EMergeVMStatus.UseTarget;

                if (forEntityVM) {
                    if (defaultMergeAction === EMergeVMStatus.UseOrigin) {
                        // attributeSet_mergeAction = EMergeAction.UseOrigin;
                        // nonStandard_mergeAction = EMergeAction.UseOrigin;
                        fam_mergeAction = EMergeVMStatus.UseOrigin;
                    }
                    else if (defaultMergeAction === EMergeVMStatus.UseTarget) {
                        // attributeSet_mergeAction = EMergeAction.UseTarget;
                        // nonStandard_mergeAction = EMergeAction.UseTarget;
                        fam_mergeAction = EMergeVMStatus.UseTarget;
                    }
                }
                break;

            case EMergeVMStatus.UseTarget:
                fam_mergeAction = EMergeVMStatus.ParentOverride;
                attributeSet_mergeAction = EMergeVMStatus.ParentOverride;
                nonStandard_mergeAction = EMergeVMStatus.ParentOverride;
                break;

            case EMergeVMStatus.UseOrigin:
                fam_mergeAction = EMergeVMStatus.ParentOverride;
                attributeSet_mergeAction = EMergeVMStatus.ParentOverride;
                nonStandard_mergeAction = EMergeVMStatus.ParentOverride;
                break;

            case EMergeVMStatus.AddOrigin:
                fam_mergeAction = EMergeVMStatus.ParentOverride;
                attributeSet_mergeAction = EMergeVMStatus.ParentOverride;
                nonStandard_mergeAction = EMergeVMStatus.ParentOverride;
                break;

            case EMergeVMStatus.RemoveTarget:
                fam_mergeAction = EMergeVMStatus.ParentOverride;
                attributeSet_mergeAction = EMergeVMStatus.ParentOverride;
                nonStandard_mergeAction = EMergeVMStatus.ParentOverride;
                break;
        }

        return fam_mergeAction;

    }


    if (forEntityVM !== null) {

        // Root
        // -----------------------------------------------------------
        forEntityVM.mergeAction = parentMergeAction;


        // Properties:
        // -----------------------------------------------------------
        forEntityVM.propertiesVM.rowsVM.forEach(property => {
            if (property.changed) {
                // TODO implement properties merge
                property.mergeAction = EMergeVMStatus.Disabled;
            } else {
                property.mergeAction = EMergeVMStatus.Disabled;
            }
        });

        forEntityVM.referencesVM?.rowsVM.forEach(property => {
            if (property.changed) {
                // TODO implement properties merge
                property.mergeAction = EMergeVMStatus.Disabled;
            } else {
                property.mergeAction = EMergeVMStatus.Disabled;
            }
        });

        forEntityVM.tiersVM?.rowsVM.forEach(property => {
            if (property.changed) {
                // TODO implement properties merge
                property.mergeAction = EMergeVMStatus.Disabled;
            } else {
                property.mergeAction = EMergeVMStatus.Disabled;
            }
        });


        // Attributes (FAM) + AttributeSet + NonStandard
        // -------------------------------------------------------------
        forEntityVM.tabsVM.forEach((tab) => {

            tab.sectionsVM.forEach((section) => {

                // (Fields) Flexible Attribute Model
                section.attributesVM.forEach((eachRow) => {
                    if (eachRow.changed)
                        eachRow.mergeAction = childMergeAction(EContext.FAM, forEntityVM, null, null);
                    else
                        eachRow.mergeAction = EMergeVMStatus.Disabled;
                });

                // Attribute Sets Model
                section.attributeSetsVM.forEach((eachMasterAttSet) => {
                    eachMasterAttSet.attributeSets.forEach((eachMasterAttributeSet) => {

                        // eachMasterAttributeSet.attributes.forEach((eachAttributeSetRow) => {
                        //     // Phase 1 - for attributes that are part of an attribute set or non-standard section,
                        //     // we will not cascade the merge action from the parent entity to the attribute level,
                        //     // and instead we will set the merge action of these attributes to undefined to indicate
                        //     // that they should not follow the parent merge action and should be decided independently,
                        //     // this is because merging at the attribute level for attributes that are part of
                        //     // an attribute set or non-standard section adds a lot of complexity to the UI and merging logic,
                        //     // and we want to keep it simple for Phase 1,
                        //     // but in the future we can consider adding this functionality if there is a need for it
                        //     // if (eachAttributeSetRow.changed)
                        //     //     eachAttributeSetRow.mergeAction = EMergeAction.UseTarget;
                        //     // else
                        //     //     eachAttributeSetRow.mergeAction = EMergeAction.Disabled;

                        //     // Phase 2
                        //     // if (childMergeAction === EMergeAction.EIgnoreByParent)
                        //     //     eachAttributeSetRow.mergeAction = childMergeAction;
                        //     // else if (eachAttributeSetRow.changed)
                        //     //     eachAttributeSetRow.mergeAction = childMergeAction;
                        //     // else
                        //     //     eachAttributeSetRow.mergeAction = undefined;
                        // });

                        if (eachMasterAttributeSet.changed)
                            eachMasterAttributeSet.mergeAction = childMergeAction(EContext.AttributeSet, null, eachMasterAttributeSet, null);
                        else
                            eachMasterAttributeSet.mergeAction = EMergeVMStatus.Disabled;

                    });
                });

                // Nonstandard Model
                section.nonStandardsVM.forEach((nonStandardVM) => {

                    const resolvedMergeAction = childMergeAction(EContext.NonStandard, null, null, nonStandardVM);

                    if (resolvedMergeAction === EMergeVMStatus.ParentOverride)
                        nonStandardVM.mergeAction = resolvedMergeAction;
                    else if (nonStandardVM.changed)
                        nonStandardVM.mergeAction = resolvedMergeAction;
                    else
                        nonStandardVM.mergeAction = EMergeVMStatus.Disabled;
                });
            });
        });
    }

    if (forAttributeSetVM != null) {

        forAttributeSetVM.mergeAction = parentMergeAction;

        // Properties:
        // -----------------------------------------------------------
        forAttributeSetVM.propertiesVM.rowsVM.forEach(property => {
            if (property.changed) {
                // TODO implement properties merge
                property.mergeAction = EMergeVMStatus.Disabled;
            } else {
                property.mergeAction = EMergeVMStatus.Disabled;
            }
        });

        // Attributes (FAM)
        // -------------------------------------------------------------
        forAttributeSetVM.attributesVM.forEach((eachAttributeSetRow) => {
            if (eachAttributeSetRow.changed)
                eachAttributeSetRow.mergeAction = EMergeVMStatus.ParentOverride
            else
                eachAttributeSetRow.mergeAction = EMergeVMStatus.Disabled;
        });

    }

    if (forNonStandardVM != null) {

        forNonStandardVM.mergeAction = parentMergeAction;

        // Properties:
        // -----------------------------------------------------------
        forNonStandardVM.propertiesVM.rowsVM.forEach(property => {
            if (property.changed) {
                // TODO implement properties merge
                property.mergeAction = EMergeVMStatus.Disabled;
            } else {
                property.mergeAction = EMergeVMStatus.Disabled;
            }
        });

        // Attributes (FAM)
        // -------------------------------------------------------------
        forNonStandardVM.attributesVM.forEach((eachNonStandardRow) => {
            if (eachNonStandardRow.changed)
                eachNonStandardRow.mergeAction = EMergeVMStatus.ParentOverride
            else
                eachNonStandardRow.mergeAction = EMergeVMStatus.Disabled;
        });
    }

}



